import React from 'react';
import { useTranslation } from 'next-i18next';
import { OfferStatus } from '@hooks/negotiations/useNegotiationStatus';
import Icon from '@components/atoms/icon/icon';
import clsx from 'clsx';
import PriceBreakDownTooltip from '@components/organisms/negotiation/negotiation-modal/price-breakdown-tooltip/price-breakdown-tooltip';
import { MinimumPricingBreakdown } from '@interfaces/pricingBreakdown';
import styles from './negotiation-listings-card-status.module.scss';

interface StatusMessageProps {
  offerStatus: OfferStatus;
  priceBreakdown?: MinimumPricingBreakdown;
  hasBestOffer?: boolean;
  isClosed?: boolean;
}

const NegotiationListingsCardStatus = ({ offerStatus, priceBreakdown, hasBestOffer, isClosed }: StatusMessageProps) => {
  const { t } = useTranslation('negotiation');

  return (
    <div className={styles.negotiationListingCard__status}>
      <div
        className={clsx(
          styles.negotiationListingCard__status__userMessage,
          styles.negotiationListingCard__status__userMessage__headline,
          styles['offerStatus__fontColor--' + offerStatus.id],
        )}
      >
        <Icon
          name={offerStatus.icon}
          className={styles.negotiationListingCard__status__icon}
          ariaHidden
        />
        {hasBestOffer && !isClosed ? (
          <span data-cy="negotiation_pages_best_offer">
            <PriceBreakDownTooltip
              isSeller={offerStatus.isSeller}
              breakdown={offerStatus.isNegoPriceShown ? priceBreakdown : undefined}
            >
              {t('NEGOTIATION_LISTING.CARD_STATUS.BEST_OFFER')}
            </PriceBreakDownTooltip>
          </span>
        ) : (
          <span data-cy="negotiation_pages_offer_message">
            <PriceBreakDownTooltip
              isSeller={offerStatus.isSeller}
              breakdown={offerStatus.isNegoPriceShown ? priceBreakdown : undefined}
            >
              {offerStatus.message}
            </PriceBreakDownTooltip>
          </span>
        )}
        {offerStatus.isNegoPriceShown && (
          <span
            className={clsx(
              styles.negotiationListingCard__status__negoPrice,
              styles['offerStatus__priceBgColor--' + offerStatus.id],
            )}
            data-cy="negotiation_pages_price"
          >
            {offerStatus.price}
          </span>
        )}
        {offerStatus.isSeller && !isClosed && (
          <span
            className={styles.negotiationListingCard__status__sellerEarnings}
            data-cy="negotiation_pages_you_earn"
          >
            ({t('NEGOTIATION_LISTING.CARD_STATUS.YOU_EARN')}
            {': '}
            {offerStatus.sellerPrice})
          </span>
        )}
      </div>
      {offerStatus.isLastOffer && (
        <div
          className={clsx(
            styles.negotiationListingCard__status__userMessage,
            styles.negotiationListingCard__status__userMessage__lastOffer,
          )}
          data-cy="negotiation_pages_last_offer"
        >
          {t('NEGOTIATION_LISTING.CARD_STATUS.RESPONSE_LAST_OFFER')}
        </div>
      )}
      <div
        className={styles.negotiationListingCard__status__userMessage}
        data-cy="negotiation_pages_user_message"
      >
        {offerStatus.subMessage}
      </div>
    </div>
  );
};

export default NegotiationListingsCardStatus;
